import "./i18n";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { AnalyticsProvider } from "@pandas/analytics";
import { deriveReservedPartnerRouteSegmentsFromRouteTree } from "@pandas/core-ui/route";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { AppErrorFallback } from "./components/AppErrorFallback";
import { ErrorBoundary } from "./components/ErrorBoundary";
import LoadingDots from "./components/LoadingDots";
import ViewportHeightPolyfill from "./components/ViewportHeightPolyfill";
import { servicesOrchestratorValues } from "./config/services";
import i18next from "./i18n";
import { initCameraRuntime } from "./lib/camera";
import { staleChunkRecovery } from "./lib/staleChunkRecovery";
import { routeTree } from "./routeTree.gen";
import { getAuth0AuthorizationParams, getAuth0CallbackErrorRedirectUrl } from "./utils/auth0";
import "@pandas/core-ui/fonts.css";
import "./index.css";

const auth0CallbackErrorRedirectUrl = getAuth0CallbackErrorRedirectUrl(window.location);
if (auth0CallbackErrorRedirectUrl) {
	window.location.replace(auth0CallbackErrorRedirectUrl);
}

// One shared reload budget for CSS/deps preloads and failed route chunks.
staleChunkRecovery.startChunkPreloadRecovery();

const cameraRuntimeLoading = initCameraRuntime();

const auth0AuthorizationParams = getAuth0AuthorizationParams(
	window.location,
	servicesOrchestratorValues.auth0.audience,
);

const queryClient = new QueryClient();
const reservedPartnerRouteSegments = deriveReservedPartnerRouteSegmentsFromRouteTree(routeTree);

const router = createRouter({
	routeTree,
	basepath: "/",
	trailingSlash: "never",
	defaultErrorComponent: AppErrorFallback,
	context: {
		auth: undefined!,
		reservedPartnerRouteSegments,
	},
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

function InnerApp() {
	const auth = useAuth0();

	if (auth.isLoading) {
		return (
			<div className="flex h-[100dvh] items-center justify-center">
				<LoadingDots />
			</div>
		);
	}

	// Sync context into the router before RouterProvider mounts so that
	// beforeLoad hooks receive the settled auth state (not the initial undefined!).
	router.update({ context: { auth, reservedPartnerRouteSegments } });

	return (
		<RouterProvider
			router={router}
			context={{ auth, reservedPartnerRouteSegments }}
		/>
	);
}

const mount = () => {
	createRoot(document.getElementById("root")!).render(
		<StrictMode>
			<ErrorBoundary>
				<ViewportHeightPolyfill />
				<Auth0Provider
					domain={servicesOrchestratorValues.auth0.domain}
					clientId={servicesOrchestratorValues.auth0.clientId}
					cacheLocation="localstorage"
					useRefreshTokens={true}
					onRedirectCallback={(appState) => {
						window.history.replaceState({}, "", appState?.returnTo ?? window.location.pathname);
					}}
					authorizationParams={auth0AuthorizationParams}
				>
					<QueryClientProvider client={queryClient}>
						<I18nextProvider i18n={i18next}>
							<AnalyticsProvider
								providers={["posthog"]}
								environment={servicesOrchestratorValues.environment}
								project="vision-ai"
								posthog={{
									apiKey: servicesOrchestratorValues.credentials.posthogKey,
									host: servicesOrchestratorValues.credentials.posthogHost,
									browserOptions: { capture_pageview: false, disable_session_recording: true },
								}}
								axiom={{
									token: servicesOrchestratorValues.credentials.axiom.token,
									orgId: servicesOrchestratorValues.credentials.axiom.orgId,
									dataset: servicesOrchestratorValues.credentials.axiom.dataset,
									environment: servicesOrchestratorValues.environment,
									project: "vision-ai",
								}}
							>
								<InnerApp />
							</AnalyticsProvider>
						</I18nextProvider>
					</QueryClientProvider>
				</Auth0Provider>
			</ErrorBoundary>
		</StrictMode>,
	);
};

// The desktop mock loads on demand and has to exist before the first camera page reads the provider.
if (!auth0CallbackErrorRedirectUrl) {
	if (cameraRuntimeLoading) void cameraRuntimeLoading.then(mount);
	else mount();
}
